import clsx from "clsx";
import { useMemo } from "react";

import Avatar from "~/components/Avatar";
import Tooltip from "~/components/Tooltip";
import type { StaffingClosingStatusesByMonth } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import { formatMonthLabel, getAggregatedStaffingClosingStatuses } from "~/modules/billing/utils/billingUtils.ts";
import type { User, UserWithConfidentialInformation } from "~/modules/user/api/user/userTypes.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = {
	closingStatuses: StaffingClosingStatusesByMonth[];
	users: User[] | UserWithConfidentialInformation[];
};

/**
 * Ring color: green when every month is closed, red when no month is closed, yellow for a mix.
 * The hover text names the open months.
 */
export function StaffingClosingStatusAvatars({ closingStatuses, users }: Props) {
	const enrichedUsers = useMemo(() => {
		return getAggregatedStaffingClosingStatuses(closingStatuses)
			.map((status) => {
				const user = users.find((u) => u.id === status.userId);
				return { ...status, fullName: user?.fullName ?? "", user };
			})
			.sort(byObjectProperty("fullName"));
	}, [closingStatuses, users]);

	return (
		<div className="flex w-full flex-row flex-wrap gap-1">
			{enrichedUsers.map(({ fullName, hasClosed, unclosedMonths, user, userId }) => {
				const isMixed = !hasClosed && unclosedMonths.length < closingStatuses.length;
				const lines = [fullName];
				if (!hasClosed) {
					lines.push(`Offen: ${unclosedMonths.map(formatMonthLabel).join(", ")}`);
				}
				return (
					<div
						className={clsx(
							"rounded-full border-4",
							hasClosed && "border-success-500",
							isMixed && "border-yellow-500",
							!hasClosed && !isMixed && "border-danger-500",
						)}
						key={"staffedUser" + userId}
					>
						<Tooltip
							className="whitespace-pre-line"
							text={lines.join("\n")}
						>
							<Avatar
								image={user?.avatarImage}
								firstName={user?.firstName}
								lastName={user?.lastName}
								width="w-10"
								height="h-10"
							/>
						</Tooltip>
					</div>
				);
			})}
		</div>
	);
}
